import { STATUS } from "@/constants/statuses";
import type { MemoryHistoryInterface } from "@apps/rewards/lib/MemoryHistory/MemoryHistoryInterface";
import { calculateMultiplier } from "@apps/rewards/utils/calculateMultiplier";
import { combineLatest, map } from "rxjs";
import type { Observable } from "rxjs";

import {
    REWARDS_LOYALTY_INFO_SPENDING_RULES_TYPES,
    REWARDS_SPENDING_RULES_REWARD_TYPES,
} from "../constants";
import type { RewardsSpendingRulesEntity } from "../models/RewardsSpendingRulesEntities";
import type {
    LoyaltyInfoSpendingRule,
    RewardsSpendingRulesPointsParams,
} from "../types";

/**
 * @description This function returns the minimum spendable points for a given spending rule.
 *
 * @param {RewardsSpendingRulesEntity} rule - The spending rule object.
 * @returns  {number} The minimum spendable points for the rule.
 */
export const getMinSpendablePoints = (
    rule: RewardsSpendingRulesEntity["rule"]
): number | null =>
    "minSpendablePoints" in rule ? rule.minSpendablePoints : null;

/**
 * @description This function returns the maximum spendable points for a given spending rule.
 *
 * @param {RewardsSpendingRulesEntity} rule - The spending rule object.
 * @returns  {number} The maximum spendable points for the rule.
 */
export const getMaxSpendablePoints = (
    rule: RewardsSpendingRulesEntity["rule"]
): number | null =>
    "maxSpendablePoints" in rule ? rule.maxSpendablePoints : null;

/**
 * @description Check if the user can redeem points based on their balance and the spending rules settings.
 *
 * @param {RewardsSpendingRulesPointsParams} params - The parameters for the function.
 * @returns {boolean} Returns true if the user can redeem, false otherwise.
 */
export const canRedeem = ({
    pointsPrice,
    userBalance = 0,
    minSpendablePoints,
}: RewardsSpendingRulesPointsParams): boolean =>
    userBalance >= (minSpendablePoints || pointsPrice);

/**
 * @description Calculate the missing points needed for a user to redeem based on their balance and the spending rules settings.
 *
 * @param {RewardsSpendingRulesPointsParams} params - The parameters for the function.
 * @returns {number} Returns the number of points needed to redeem.
 */
export const getMissingPoints = ({
    pointsPrice,
    userBalance = 0,
    minSpendablePoints,
}: RewardsSpendingRulesPointsParams): number =>
    (minSpendablePoints || pointsPrice) - userBalance;

/**
 * @description Check if the current view is the same as the given path.
 *
 * @param {string} currentPath - The path to check against.
 * @param {MemoryHistoryInterface} history - The history object to check the current location.
 * @returns {boolean} Returns true if the current view matches the given path, false otherwise.
 */
export const isCurrentView = (
    currentPath: string,
    history: MemoryHistoryInterface
): boolean => history.location.pathname === currentPath;

/**
 * @description Get the loading status of the redeem process.
 *
 * @returns {boolean} Returns true if the redeem process is loading, false otherwise.
 */
export const getRedeemLoadingStatus = (
    activeRewardsRequestStatus: STATUS,
    isRedeemLoading: boolean
): boolean =>
    activeRewardsRequestStatus === STATUS.LOADING ||
    activeRewardsRequestStatus === STATUS.INITIAL_LOADING ||
    isRedeemLoading;

/**
 * @description Filters the spending rules list based on whether the user is a customer and rule has customer whitelist tags.
 *
 * @param {Observable<RewardsSpendingRulesEntity[]>} spendingRulesList$
 * @param {Observable<boolean>} isCustomer$
 * @returns {Observable<RewardsSpendingRulesEntity[]>} Filtered list of spending rules.
 */
export const selectFilteredSpendingRulesList = (
    spendingRulesList$: Observable<RewardsSpendingRulesEntity[]>,
    isCustomer$: Observable<boolean>
): Observable<RewardsSpendingRulesEntity[]> =>
    combineLatest([spendingRulesList$, isCustomer$]).pipe(
        map(([list, isCustomer]) =>
            isCustomer
                ? list
                : list.filter(
                      (item) => !item.rule.customerTags.whitelist.length
                  )
        )
    );

/**
 * Resolves the most expensive spending rule the user can afford based on their point balance.
 * If no rule is affordable, returns the closest (cheapest) one and how many points are missing.
 *
 * @param {RewardsSpendingRulesEntity[]} rules - List of available spending rules.
 * @param {number} [userBalance=0] - The user's current point balance.
 * @returns {LoyaltyInfoSpendingRule | null} The best matching spending rule,
 *          or null if the list is empty.
 */
export const getExpensiveOrNextSpendingRule = (
    rules: RewardsSpendingRulesEntity[],
    userBalance = 0
): LoyaltyInfoSpendingRule | null => {
    if (!rules.length) return null;

    let maxAffordableRule: RewardsSpendingRulesEntity | null = null;
    let maxAffordablePoints: number | null = null;
    let minUnaffordableRule: RewardsSpendingRulesEntity | null = null;
    let minUnaffordablePoints: number | null = null;

    for (const ruleEntity of rules) {
        const actualPointsPrice = ruleEntity.rule.pointsPrice;
        const minSpendable = getMinSpendablePoints(ruleEntity.rule);
        const requiredPoints =
            minSpendable !== null && minSpendable > 0
                ? minSpendable
                : actualPointsPrice;
        const isAffordable = userBalance >= requiredPoints;

        if (isAffordable) {
            if (
                maxAffordablePoints === null ||
                requiredPoints > maxAffordablePoints
            ) {
                maxAffordablePoints = requiredPoints;
                maxAffordableRule = ruleEntity;
            }
        } else {
            if (
                minUnaffordablePoints === null ||
                requiredPoints < minUnaffordablePoints
            ) {
                minUnaffordablePoints = requiredPoints;
                minUnaffordableRule = ruleEntity;
            }
        }
    }

    if (maxAffordableRule && maxAffordablePoints !== null) {
        return {
            type: REWARDS_LOYALTY_INFO_SPENDING_RULES_TYPES.expensive,
            rule: maxAffordableRule,
            pointsPrice: maxAffordablePoints,
        };
    }

    if (minUnaffordableRule && minUnaffordablePoints !== null) {
        return {
            type: REWARDS_LOYALTY_INFO_SPENDING_RULES_TYPES.notEnoughPoints,
            rule: minUnaffordableRule,
            pointsPrice: minUnaffordablePoints,
            missingPoints: minUnaffordablePoints - userBalance,
        };
    }

    return null;
};

/**
 * Calculates the maximum discount a user can redeem for a given spending rule,
 * based on their current point balance and rule constraints.
 *
 * - For flexible amount discounts: returns the maximum redeemable amount.
 * - For flexible percentage discounts: returns the maximum redeemable percentage,
 *   capped by the user’s points balance and percentage limit.
 * - For other reward types: returns `undefined`.
 *
 * @param {RewardsSpendingRulesEntity} rule - The spending rule to evaluate.
 * @param {number} pointsBalance - The user’s available points balance.
 * @param {number} [percentageLimit=100] - Optional cap on percentage-based discounts.
 * @returns {number | undefined} The maximum discount value, or `undefined`
 * if the rule is not a flexible discount type.
 */
export function calculateMaxDiscount(
    rule: RewardsSpendingRulesEntity,
    pointsBalance: number,
    percentageLimit = 100
): number | undefined {
    const {
        rule: { pointsPrice },
        rewards: [reward],
    } = rule;

    const maxSpendablePoints = getMaxSpendablePoints(rule.rule);
    const maxPointsLimit = maxSpendablePoints
        ? Math.min(maxSpendablePoints, pointsBalance)
        : pointsBalance;

    let discount = 0;
    let isFlexibleAmount = false;
    let isFlexiblePercentage = false;

    switch (reward.rewardType) {
        case REWARDS_SPENDING_RULES_REWARD_TYPES.flexibleAmountDiscount:
            discount = reward.amountY;
            isFlexibleAmount = true;
            break;
        case REWARDS_SPENDING_RULES_REWARD_TYPES.flexiblePercentageDiscount:
            discount = reward.percentsY;
            isFlexiblePercentage = true;
            break;
    }

    const discountMultiplier = calculateMultiplier(percentageLimit, discount);
    const maxPointsMultiplier = calculateMultiplier(
        maxPointsLimit,
        pointsPrice
    );

    const pointsOrDiscountMultiplier = isFlexibleAmount
        ? maxPointsMultiplier
        : isFlexiblePercentage
        ? Math.min(maxPointsMultiplier, discountMultiplier)
        : 0;

    return isFlexibleAmount || isFlexiblePercentage
        ? pointsOrDiscountMultiplier * discount
        : undefined;
}
